import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { api } from '../services/api';
import SafeIcon from '../common/SafeIcon';

const OrderForm = () => {
  const { slug } = useParams();
  const { data, refreshData, t, formatUnit } = useAppContext();
  
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [quantities, setQuantities] = useState({});
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [identifiedCustomer, setIdentifiedCustomer] = useState(null);

  // Mode Detection: If a slug is present, it's External Mode. Otherwise, Internal Mode.
  const isExternalMode = !!slug;

  // Identify customer from slug in External Mode
  useEffect(() => {
    if (isExternalMode && data.customers.length > 0) {
      const customer = data.customers.find(c => c.active && c.slug === slug);
      if (customer) {
        setIdentifiedCustomer(customer);
        setSelectedCustomerId(customer.id);
      } else {
        setIdentifiedCustomer('invalid');
      }
    } else if (!isExternalMode) {
      // Reset for internal mode to allow manual selection
      setIdentifiedCustomer(null);
    }
  }, [slug, data.customers, isExternalMode]);

  // Derive products assigned to the active customer (selected manually or via slug)
  const customerProducts = useMemo(() => {
    if (!selectedCustomerId) return [];
    const assignedIds = data.customerProducts
      .filter(cp => cp.customer_id === selectedCustomerId)
      .map(cp => cp.product_id);
    return data.products.filter(p => p.active && p.visible_on_order_form && assignedIds.includes(p.id));
  }, [selectedCustomerId, data.customerProducts, data.products]);

  // Group products by category
  const groupedProducts = useMemo(() => {
    const groups = { Bread: [], Cakes: [], Pastry: [], 'Bløddej': [] };
    customerProducts.forEach(p => {
      if (!groups[p.category]) groups[p.category] = [];
      groups[p.category].push(p);
    });
    return groups;
  }, [customerProducts]);

  const handleQtyChange = (productId, val) => {
    const num = parseInt(val, 10);
    setQuantities(prev => ({ ...prev, [productId]: isNaN(num) || num < 0 ? '' : num }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomerId || !deliveryDate) return alert('Please select customer and date.');
    
    const lines = Object.entries(quantities)
      .filter(([id, qty]) => qty > 0)
      .map(([id, qty]) => ({ product_id: id, quantity: qty }));
      
    if (lines.length === 0) return alert('Please enter at least one quantity.');
    setIsSubmitting(true);
    
    const order = {
      customer_id: selectedCustomerId,
      order_date: new Date().toISOString().split('T')[0],
      delivery_date: deliveryDate,
      status: 'New',
      notes
    };
    
    await api.saveOrder(order, lines);
    await refreshData();
    setIsSubmitting(false);
    setSuccess(true);
    
    // Reset form
    setQuantities({});
    setNotes('');
    setTimeout(() => setSuccess(false), 5000);
  };

  // Render Fallback for invalid external links
  if (isExternalMode && identifiedCustomer === 'invalid') {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-red-50">
          <div className="w-20 h-20 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="AlertTriangle" className="w-10 h-10" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('orderForm.invalidLink')}</h1>
          <p className="text-gray-500 max-w-md mx-auto">{t('orderForm.invalidLinkDesc')}</p>
          {!isExternalMode && (
            <a href="/" className="inline-block mt-8 text-amber-600 font-medium hover:underline">Return to Dashboard</a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="bg-amber-600 p-8 text-white text-center">
          <SafeIcon name="Briefcase" className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl font-bold">{t('orderForm.title')}</h1>
          <p className="text-amber-100 mt-2">
            {identifiedCustomer ? identifiedCustomer.name : (isExternalMode ? t('orderForm.subtitle') : 'Internal Order Management')}
          </p>
        </div>
        
        {success ? (
          <div className="p-12 text-center animate-fade-in">
            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon name="Check" className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('orderForm.success.title')}</h2>
            <p className="text-gray-500 mb-8">{t('orderForm.success.desc')}</p>
            <button onClick={() => setSuccess(false)} className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors">
              {t('orderForm.success.button')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            <div className={`grid gap-6 ${isExternalMode ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
              
              {/* Internal Mode: Show customer selection dropdown */}
              {!isExternalMode && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('orderForm.selectAccount')}</label>
                  <select 
                    required 
                    value={selectedCustomerId} 
                    onChange={(e) => { setSelectedCustomerId(e.target.value); setQuantities({}); }} 
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none bg-gray-50 text-sm"
                  >
                    <option value="">{t('orderForm.chooseAccount')}</option>
                    {data.customers.filter(c => c.active).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Delivery Date: Always visible */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('orderForm.deliveryDate')}</label>
                <input 
                  type="date" 
                  required 
                  value={deliveryDate} 
                  onChange={(e) => setDeliveryDate(e.target.value)} 
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none bg-gray-50 text-sm" 
                />
              </div>
            </div>
            
            {/* Warning if a customer is selected but has no products assigned */}
            {selectedCustomerId && customerProducts.length === 0 && (
              <div className="p-6 bg-yellow-50 text-yellow-800 rounded-xl border border-yellow-200 text-center text-sm">
                {t('orderForm.noProducts')}
              </div>
            )}
            
            {/* Product List: Visible once a customer is identified or selected */}
            {selectedCustomerId && customerProducts.length > 0 && (
              <div className="space-y-8">
                {['Bread', 'Cakes', 'Pastry', 'Bløddej'].map(category => {
                  const items = groupedProducts[category] || [];
                  if (items.length === 0) return null;
                  return (
                    <div key={category} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-bold text-gray-800 text-sm">
                        {category}
                      </div>
                      <div className="divide-y divide-gray-100">
                        {items.map(p => (
                          <div key={p.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                            <div className="pr-4">
                              <p className="font-medium text-gray-900 text-sm">{p.name}</p>
                              {p.notes && <p className="text-xs text-gray-500">{p.notes}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              <input 
                                type="number" 
                                min="0" 
                                placeholder="0" 
                                value={quantities[p.id] || ''} 
                                onChange={(e) => handleQtyChange(p.id, e.target.value)} 
                                className="w-16 p-2 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none text-sm font-bold" 
                              />
                              <span className="text-xs text-gray-500 w-10 font-medium">{formatUnit(p.unit)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('orderForm.notesLabel')}</label>
                  <textarea 
                    value={notes} 
                    onChange={(e) => setNotes(e.target.value)} 
                    rows="3" 
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none bg-gray-50 text-sm" 
                    placeholder={t('orderForm.notesPlaceholder')}
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="w-full py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                >
                  {isSubmitting ? t('orderForm.submitting') : t('orderForm.submit')}
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default OrderForm;